import { Injectable, inject } from '@angular/core';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Observable, catchError, of, tap, timeout } from 'rxjs';



import { environment } from '../../environments/environment';

import { DneRuleRequestFields } from '../shared/utils/dne-rule';

import {

  VendorBillsApiResponse,

  AdminEstimateNegotiation,

  AdminSendCounterRequest,

  CustomerRejectionDecision,

  NegotiationRound,

} from '../models/vendor-bills.model';



@Injectable({ providedIn: 'root' })

export class NegotiatingAgentService {

  private readonly http = inject(HttpClient);

  private readonly base = `${environment.apiBaseUrl}/api/v1/admin/vendor-bills`;

  private static readonly TIMEOUT_MS = 60_000;

  private static readonly POLL_MS = 1_500;

  private static readonly MAX_POLLS = 10;



  getNegotiation(estimateKey: string): Observable<VendorBillsApiResponse<AdminEstimateNegotiation>> {

    return this.http

      .get<VendorBillsApiResponse<AdminEstimateNegotiation>>(

        `${this.base}/estimates/${estimateKey}/negotiation`,

      )

      .pipe(

        timeout(NegotiatingAgentService.TIMEOUT_MS),

        catchError((err) =>
          of(this.mapHttpError<AdminEstimateNegotiation>(err, 'Unable to load negotiation recommendations.')),
        ),

      );

  }

  /** Every saved round of an on-site negotiation, oldest first (empty until history is switched on). */
  getNegotiationRounds(estimateKey: string): Observable<VendorBillsApiResponse<NegotiationRound[]>> {
    return this.http
      .get<VendorBillsApiResponse<NegotiationRound[]>>(`${this.base}/estimates/${estimateKey}/rounds`)
      .pipe(
        timeout(NegotiatingAgentService.TIMEOUT_MS),
        catchError((err) => of(this.mapHttpError<NegotiationRound[]>(err, 'Unable to load negotiation history.'))),
      );
  }



  /** Poll until recommendationsReady or max attempts (one result per estimate). */
  pollNegotiationUntilReady(
    estimateKey: string,
  ): Observable<{ data: AdminEstimateNegotiation | null; errorMessage: string }> {
    return new Observable<{ data: AdminEstimateNegotiation | null; errorMessage: string }>((subscriber) => {
      let attempts = 0;
      let lastError = '';

      const poll = (): void => {
        if (attempts >= NegotiatingAgentService.MAX_POLLS) {
          subscriber.next({ data: null, errorMessage: lastError });
          subscriber.complete();
          return;
        }

        attempts++;
        this.getNegotiation(estimateKey).subscribe({
          next: (res) => {
            if (!res?.status) {
              lastError = res?.message || lastError || 'Could not load agent recommendations.';
            }

            const data = res?.status ? res.data : null;

            if (data?.errorMessage && !data.recommendationsReady) {
              subscriber.next({ data, errorMessage: data.errorMessage });
              subscriber.complete();
              return;
            }

            const ready = res?.status && data?.recommendationsReady;
            if (ready) {
              subscriber.next({ data, errorMessage: '' });
              subscriber.complete();
              return;
            }

            if (attempts >= NegotiatingAgentService.MAX_POLLS) {
              subscriber.next({
                data: res?.status ? (data ?? null) : null,
                errorMessage: lastError || data?.errorMessage || 'Could not load agent recommendations.',
              });
              subscriber.complete();
              return;
            }

            setTimeout(poll, NegotiatingAgentService.POLL_MS);
          },
          error: () => {
            subscriber.next({
              data: null,
              errorMessage: lastError || 'Could not load agent recommendations.',
            });
            subscriber.complete();
          },
        });
      };

      poll();
    });
  }



  /**
   * Tells the agent what the admin did with its customer-rejection plan, which closes the plan.
   * Called AFTER the action itself has run — a revision request in particular, which the agent still
   * accepts while the plan is open. Clears the plan from the estimate card either way.
   */
  recordCustomerRejectionDecision(
    estimateKey: string,
    decision: CustomerRejectionDecision,
    actionTaken?: string | null,
  ): Observable<VendorBillsApiResponse<AdminEstimateNegotiation>> {
    return this.http
      .post<VendorBillsApiResponse<AdminEstimateNegotiation>>(
        `${this.base}/estimates/${estimateKey}/negotiation/customer-rejection-decision`,
        { decision, actionTaken: actionTaken ?? null },
      )
      .pipe(
        timeout(NegotiatingAgentService.TIMEOUT_MS),
        catchError((err) =>
          of(this.mapHttpError<AdminEstimateNegotiation>(err, 'Failed to record the decision.')),
        ),
      );
  }

  sendCounter(

    estimateKey: string,

    body: AdminSendCounterRequest,

  ): Observable<VendorBillsApiResponse<AdminEstimateNegotiation>> {

    return this.http

      .post<VendorBillsApiResponse<AdminEstimateNegotiation>>(

        `${this.base}/estimates/${estimateKey}/send-counter`,

        body,

      )

      .pipe(

        timeout(NegotiatingAgentService.TIMEOUT_MS),

        catchError((err) =>
          of(this.mapHttpError<AdminEstimateNegotiation>(err, 'Failed to send counter.')),
        ),

      );

  }



  acceptAll(

    estimateKey: string,

    notesToVendor?: string | null,

    dne?: DneRuleRequestFields,

  ): Observable<VendorBillsApiResponse<AdminEstimateNegotiation>> {

    return this.http

      .post<VendorBillsApiResponse<AdminEstimateNegotiation>>(

        `${this.base}/estimates/${estimateKey}/accept-all`,

        {

          notesToVendor: notesToVendor ?? null,

          keepAllVendors: dne?.keepAllVendors ?? false,


        },

      )

      .pipe(

        timeout(NegotiatingAgentService.TIMEOUT_MS),

        catchError((err) =>
          of(this.mapHttpError<AdminEstimateNegotiation>(err, 'Failed to accept all recommendations.')),
        ),

      );

  }

  private mapHttpError<T>(err: unknown, fallback: string): VendorBillsApiResponse<T> {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as (Partial<VendorBillsApiResponse<T>> & { detail?: unknown }) | null;
      // A refusal without the ApiResponse envelope (ProblemDetails) still carries its reason in `detail`.
      const message =
        body && typeof body === 'object'
          ? typeof body.message === 'string'
            ? body.message
            : typeof body.detail === 'string' && body.detail.trim()
              ? body.detail.trim()
              : null
          : null;
      if (body && typeof body === 'object' && message !== null) {
        return {
          status: false,
          responseCode: body.responseCode ?? err.status,
          message,
          data: (body.data ?? null) as T,
          details: body.details ?? [],
          unixTime: body.unixTime ?? 0,
          traceId: body.traceId ?? null,
        };
      }

      return {
        status: false,
        responseCode: err.status,
        message: err.statusText || fallback,
        data: null as unknown as T,
        details: [],
        unixTime: 0,
        traceId: null,
      };
    }

    return {
      status: false,
      responseCode: 0,
      message: fallback,
      data: null as unknown as T,
      details: [],
      unixTime: 0,
      traceId: null,
    };
  }

}


